import { useMemo, useState } from "react";

import { getLoanRaydiumCollateral } from "@bridgesplit/abf-react";
import { Column, TabsSelect, usePolling } from "@bridgesplit/ui";
import { Slippage } from "app/components/common";
import { IS_LOCAL_NX_DEV } from "@bridgesplit/utils";

import { ActionProps } from "../types";
import { RaydiumProvider, useRaydiumContext } from "./RaydiumContext";
import { useInitLuts, useInitPoolData, useInitPositionData } from "./util";
import RaydiumInfo from "./RaydiumInfo";
import { OpenPosition } from "./open";
import { Deposit } from "./deposit";
import { Withdraw } from "./withdraw";
import { useRaydiumDepositHooks } from "./deposit/util";
import { useWithdrawRaydiumHooks } from "./withdraw/util";
import { useRaydiumOpenPositionHooks } from "./open/util";

enum Open {
    Deposit = "Deposit",
    Withdraw = "Withdraw",
    OpenPosition = "Re-initialize"
}

export default function ManageRaydium({ loanExpanded }: ActionProps) {
    const raydiumCollateral = getLoanRaydiumCollateral(loanExpanded);

    const [open, setOpen] = useState<Open>(Open.Deposit);

    const Section = useMemo(() => {
        switch (open) {
            case Open.OpenPosition:
                return OpenPosition;
            case Open.Deposit:
                return Deposit;
            case Open.Withdraw:
                return Withdraw;
            default:
                return null;
        }
    }, [open]);

    if (!raydiumCollateral || !loanExpanded || !Section) return null;

    return (
        <RaydiumProvider value={{ raydiumPosition: raydiumCollateral, loanExpanded }}>
            <SlippageProvider>
                <InitPositionData />
                <Column spacing={4} flexGrow={1} justifyContent="space-between">
                    <Column spacing={3}>
                        <RaydiumInfo />
                        <TabsSelect
                            wrapperSx={{ mx: -2, width: "auto" }}
                            value={open}
                            setValue={setOpen}
                            options={Object.values(Open).map((o) => ({ value: o }))}
                        />
                        <Section.Top />
                    </Column>
                    <Section.Bottom />
                </Column>
            </SlippageProvider>
        </RaydiumProvider>
    );
}

function SlippageProvider({ children }: { children: React.ReactNode }) {
    const { slippageController } = useRaydiumContext();
    return <Slippage.Wrapper {...slippageController.slippageWrapperProps}>{children}</Slippage.Wrapper>;
}

function InitPositionData() {
    const { poll } = useRaydiumContext();

    usePolling({ callback: poll, interval: 15_000, skip: IS_LOCAL_NX_DEV });
    useInitLuts();
    useInitPositionData();
    useInitPoolData();
    useRaydiumOpenPositionHooks();
    useRaydiumDepositHooks();
    useWithdrawRaydiumHooks();

    return null;
}
